import { useWorkspaceContext } from '@/contexts/WorkspaceContext';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { 
  Inbox, 
  MessageSquare, 
  CheckCircle2, 
  Clock,
  ArrowRight
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { RetryErrorCard } from '@/components/ui/retry-error-card';
import { useDashboardActivities } from '@/hooks/useDashboardQueries';

const activityConfig = {
  new_ticket: {
    icon: Inbox,
    label: 'Nouveau ticket',
    color: 'text-luminjo-pink',
    bgColor: 'bg-luminjo-pink/10',
  },
  customer_reply: {
    icon: MessageSquare,
    label: 'Réponse client',
    color: 'text-primary',
    bgColor: 'bg-primary/10',
  },
  closed: {
    icon: CheckCircle2,
    label: 'Ticket fermé',
    color: 'text-luminjo-success',
    bgColor: 'bg-luminjo-success/10',
  },
  status_change: {
    icon: Clock,
    label: 'Statut modifié',
    color: 'text-accent',
    bgColor: 'bg-accent/10',
  },
};

// Status translations for display
const statusTranslations: Record<string, string> = {
  open: 'Ouvert',
  pending: 'En attente',
  resolved: 'Résolu',
  closed: 'Fermé',
};

export function RecentActivity() {
  const { activeWorkspace } = useWorkspaceContext();

  const { data: activities, isLoading, error, refetch, isRefetching } = useDashboardActivities(
    activeWorkspace?.id
  );

  if (isLoading) {
    return (
      <div className="bg-card rounded-lg border border-border shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <Skeleton className="h-5 w-32" />
        </div>
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="flex items-start gap-3">
              <Skeleton className="h-8 w-8 rounded-full" />
              <div className="flex-1 space-y-1">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-3 w-24" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error && !activities) {
    return (
      <div className="bg-card rounded-lg border border-border shadow-sm">
        <RetryErrorCard
          message="Impossible de charger les activités"
          onRetry={() => refetch()}
          isRetrying={isRefetching}
          retryCount={0}
        />
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg border border-border shadow-sm p-6 animate-in fade-in-0 duration-200">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-foreground">Activité récente</h3>
        <Button variant="ghost" size="sm" asChild>
          <Link to="/tickets" className="flex items-center gap-1 text-xs">
            Voir tout
            <ArrowRight className="h-3 w-3" />
          </Link>
        </Button>
      </div>

      {!activities || activities.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-4">
          Aucune activité récente
        </p>
      ) : (
        <div className="space-y-2">
          {activities.map((activity) => {
            const config = activityConfig[activity.type];
            const Icon = config.icon;

            return (
              <Link
                key={activity.id}
                to={`/tickets/${activity.ticketId}`}
                className="flex items-start gap-3 group hover:bg-muted/50 -mx-2 px-2 py-2 rounded-lg transition-colors"
              >
                <div className={`p-1.5 rounded-full ${config.bgColor}`}>
                  <Icon className={`h-4 w-4 ${config.color}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="text-xs font-normal">
                      {config.label}
                    </Badge>
                    {activity.metadata?.newStatus && (
                      <span className="text-xs text-muted-foreground">
                        → {statusTranslations[activity.metadata.newStatus] || activity.metadata.newStatus}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-foreground truncate group-hover:text-primary transition-colors">
                    {activity.ticketSubject}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {formatDistanceToNow(new Date(activity.timestamp), { 
                      addSuffix: true, 
                      locale: fr 
                    })}
                  </p>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
